/**
 * GraphQL operations for admin bulk reanalysis (project_management#514).
 *
 * `previewReanalysisBatch` runs the SAME planner as the mutation and writes
 * nothing, so the confirmation dialog can show the real item count, the skips
 * with reasons, and the ETA before the admin commits.
 *
 * #574: a recording batch may span several patients. The preview reports the
 * per-patient breakdown (owners come from the server, never from the page),
 * batches list their patients, items carry their patient, and failed items
 * can be retried as a new batch.
 */
import { gql } from '@apollo/client';

export const PREVIEW_REANALYSIS_BATCH = gql`
  query PreviewReanalysisBatch($input: CreateReanalysisBatchInput!) {
    previewReanalysisBatch(input: $input) {
      success
      error
      plannedCount
      concurrency
      estimatedSeconds
      estimatedDuration
      patientCount
      multiPatient
      maxItems
      patients {
        userId
        plannedCount
        skippedCount
      }
      skippedByReason {
        reason
        count
      }
      skipped {
        targetId
        reason
      }
    }
  }
`;

const BATCH_FIELDS = gql`
  fragment ReanalysisBatchFields on ReanalysisBatch {
    id
    createdBy
    targetUserId
    targetUserIds
    patientCount
    multiPatient
    retryOf
    scope
    status
    reason
    concurrency
    startAfter
    estimatedSeconds
    createdAt
    startedAt
    finishedAt
    counts {
      total
      pending
      inFlight
      succeeded
      failed
      skipped
    }
  }
`;

export const CREATE_REANALYSIS_BATCH = gql`
  mutation CreateReanalysisBatch($input: CreateReanalysisBatchInput!) {
    createReanalysisBatch(input: $input) {
      success
      error
      batch {
        ...ReanalysisBatchFields
      }
    }
  }
  ${BATCH_FIELDS}
`;

export const GET_REANALYSIS_BATCHES = gql`
  query GetReanalysisBatches(
    $limit: Int!
    $offset: Int!
    $status: ReanalysisBatchStatus
    $targetUserId: String
  ) {
    getReanalysisBatches(
      limit: $limit
      offset: $offset
      status: $status
      targetUserId: $targetUserId
    ) {
      success
      error
      totalCount
      batches {
        ...ReanalysisBatchFields
      }
    }
  }
  ${BATCH_FIELDS}
`;

export const GET_REANALYSIS_BATCH = gql`
  query GetReanalysisBatch(
    $batchId: MongoObjectID!
    $itemState: ReanalysisItemState
    $itemUserId: String
    $itemLimit: Int
    $itemOffset: Int
  ) {
    getReanalysisBatch(
      batchId: $batchId
      itemState: $itemState
      itemUserId: $itemUserId
      itemLimit: $itemLimit
      itemOffset: $itemOffset
    ) {
      success
      error
      batch {
        ...ReanalysisBatchFields
        itemsTotal
        items {
          id
          targetId
          userId
          fileName
          state
          attempts
          weight
          publishedAt
          completedAt
          durationMs
          error
        }
      }
    }
  }
  ${BATCH_FIELDS}
`;

export const RETRY_FAILED_REANALYSIS_ITEMS = gql`
  mutation RetryFailedReanalysisItems($batchId: MongoObjectID!) {
    retryFailedReanalysisItems(batchId: $batchId) {
      success
      error
      batch {
        ...ReanalysisBatchFields
      }
    }
  }
  ${BATCH_FIELDS}
`;

export const PAUSE_REANALYSIS_BATCH = gql`
  mutation PauseReanalysisBatch($batchId: MongoObjectID!) {
    pauseReanalysisBatch(batchId: $batchId) {
      success
      error
      batch {
        ...ReanalysisBatchFields
      }
    }
  }
  ${BATCH_FIELDS}
`;

export const RESUME_REANALYSIS_BATCH = gql`
  mutation ResumeReanalysisBatch($batchId: MongoObjectID!) {
    resumeReanalysisBatch(batchId: $batchId) {
      success
      error
      batch {
        ...ReanalysisBatchFields
      }
    }
  }
  ${BATCH_FIELDS}
`;

export const CANCEL_REANALYSIS_BATCH = gql`
  mutation CancelReanalysisBatch($batchId: MongoObjectID!) {
    cancelReanalysisBatch(batchId: $batchId) {
      success
      error
      batch {
        ...ReanalysisBatchFields
      }
    }
  }
  ${BATCH_FIELDS}
`;
